import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';

import {
  getEntry, createEntry, updateEntry, setEntryComplete, deleteEntry,
  getEntryComments, addEntryComment, deleteEntryComment,
  uploadEntryAttachment, deleteEntryAttachment, downloadEntryAttachment,
} from '@/services/marketingCalendarService';

const ACCENT = '#c2410c';

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtCommentTime = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d) ? '' : d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  });
};

const ContentEntryModal = ({ mode, entryId, defaultDate, staff, onClose, onSaved }) => {
  const isCreate = mode === 'create';
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    title: '', notes: '', due_date: defaultDate || '', assignees: [],
  });
  const [completed, setCompleted] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);

  const reload = useCallback(async () => {
    if (isCreate) return;
    setLoading(true);
    try {
      const e = await getEntry(entryId);
      setForm({
        title: e.title || '',
        notes: e.notes || '',
        due_date: e.due_date || '',
        assignees: (e.assignees || []).map((a) => a.id),
      });
      setCompleted(!!e.is_completed);
      setAttachments(e.attachments || []);
      setComments(e.comments || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [isCreate, entryId]);

  useEffect(() => { reload(); }, [reload]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAssignee = (id) =>
    setForm((f) => ({
      ...f,
      assignees: f.assignees.includes(id)
        ? f.assignees.filter((x) => x !== id)
        : [...f.assignees, id],
    }));

  const save = async () => {
    if (!form.title.trim()) { setError('Please enter a title.'); return; }
    if (!form.due_date) { setError('Please choose a date.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: form.title.trim(),
        notes: form.notes,
        due_date: form.due_date,
        assignees: form.assignees,
      };
      if (isCreate) await createEntry(body);
      else await updateEntry(entryId, body);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const toggleComplete = async () => {
    const next = !completed;
    setBusy(true);
    try {
      await setEntryComplete(entryId, next);
      setCompleted(next);
      onSaved?.();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteEntry(entryId);
      onSaved?.();
      onClose();
    } catch (err) { setError(err.message); setBusy(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      let latest = attachments;
      for (const f of files) {
        const res = await uploadEntryAttachment(entryId, f);
        latest = res.attachments || latest;
      }
      setAttachments(latest);
    } catch (err) { setError(err.message); }
    setBusy(false);
    e.target.value = '';
  };

  const removeAttachment = async (attId) => {
    setBusy(true);
    try {
      await deleteEntryAttachment(attId);
      setAttachments((a) => a.filter((x) => x.id !== attId));
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text || commentBusy) return;
    setCommentBusy(true);
    try {
      await addEntryComment(entryId, text);
      setCommentText('');
      setComments(await getEntryComments(entryId));
    } catch (err) { setError(err.message); }
    setCommentBusy(false);
  };

  const removeComment = async (commentId) => {
    try {
      await deleteEntryComment(commentId);
      setComments(await getEntryComments(entryId));
    } catch (err) { setError(err.message); }
  };

  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide';
  const inputCls =
    'mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full max-h-[92vh] flex flex-col ${isCreate ? 'max-w-lg' : 'max-w-5xl'}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">
            {isCreate ? 'New content' : 'Content'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-10"><CircularProgress size={26} style={{ color: ACCENT }} /></div>
          ) : (
            <div className={isCreate ? '' : 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] items-start'}>
              <div className="space-y-4 min-w-0">
                {!isCreate && (
                  <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={completed}
                      onChange={toggleComplete}
                      disabled={busy}
                      className="w-4 h-4 rounded accent-green-600"
                    />
                    <span className={`text-sm font-semibold ${completed ? 'text-green-700' : 'text-gray-700'}`}>
                      {completed ? 'Completed' : 'Mark as done'}
                    </span>
                  </label>
                )}

                <div>
                  <label className={labelCls}>Title</label>
                  <input
                    autoFocus
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                    placeholder=""
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Date</label>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => set('due_date', e.target.value)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Assigned to</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {staff.map((s) => {
                      const on = form.assignees.includes(s.Id);
                      return (
                        <button
                          key={s.Id}
                          type="button"
                          onClick={() => toggleAssignee(s.Id)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                            on ? 'text-white' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}
                          style={on ? { backgroundColor: s.task_color || ACCENT, borderColor: s.task_color || ACCENT } : undefined}
                        >
                          {s.Name}
                        </button>
                      );
                    })}
                    {staff.length === 0 && (
                      <p className="text-xs text-gray-400 italic">
                        No marketing staff yet — tick people on the Group Managers roster.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Notes</label>
                  <textarea
                    rows={5}
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    placeholder="Copy, links, brief…"
                    className={`${inputCls} resize-none`}
                  />
                </div>

                {!isCreate && (
                  <div>
                    <div className="flex items-center justify-between">
                      <label className={labelCls}>Attachments</label>
                      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                        className="flex items-center gap-1 text-xs font-semibold text-orange-700 hover:text-orange-900 disabled:opacity-50">
                        <AttachFileIcon style={{ fontSize: 14 }} /> Add file
                      </button>
                      <input ref={fileRef} type="file" multiple className="hidden" onChange={handleUpload} />
                    </div>
                    <div className="mt-1.5 space-y-1.5">
                      {attachments.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No files attached.</p>
                      ) : attachments.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                          <AttachFileIcon style={{ fontSize: 14 }} className="text-gray-400 flex-shrink-0" />
                          <button type="button" onClick={() => downloadEntryAttachment(a.id, a.original_name)}
                            className="flex-1 min-w-0 text-left text-sm text-gray-700 hover:text-orange-700 truncate">
                            {a.original_name}
                          </button>
                          <span className="text-[10px] text-gray-400">{fmtSize(a.size_bytes)}</span>
                          <button type="button" onClick={() => downloadEntryAttachment(a.id, a.original_name)}
                            className="text-gray-400 hover:text-orange-700"><DownloadIcon style={{ fontSize: 15 }} /></button>
                          <button type="button" onClick={() => removeAttachment(a.id)}
                            className="text-gray-400 hover:text-red-600"><DeleteOutlineIcon style={{ fontSize: 15 }} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {error && <p className="text-sm text-red-500">{error}</p>}
              </div>

              {!isCreate && (
                <div className="min-w-0 lg:border-l lg:border-gray-100 lg:pl-6">
                  <label className={labelCls}>
                    <span className="inline-flex items-center gap-1">
                      <ChatBubbleOutlineIcon style={{ fontSize: 14 }} />
                      Comments{comments.length > 0 ? ` (${comments.length})` : ''}
                    </span>
                  </label>

                  <div className="mt-1.5 flex gap-1.5">
                    <input
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                      placeholder="Add a comment…"
                      disabled={commentBusy}
                      className="flex-1 min-w-0 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 disabled:opacity-50"
                    />
                    <button type="button" onClick={submitComment} disabled={commentBusy || !commentText.trim()}
                      className="flex items-center px-2.5 rounded-lg bg-orange-700 text-white text-xs font-semibold hover:bg-orange-800 disabled:opacity-40">
                      {commentBusy ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <SendIcon style={{ fontSize: 14 }} />}
                    </button>
                  </div>

                  <div className="mt-2 space-y-1.5 max-h-[60vh] overflow-y-auto pr-0.5">
                    {comments.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No comments yet.</p>
                    ) : comments.map((cm) => (
                      <div key={cm.id} className="group flex items-start gap-2 rounded-lg px-2.5 py-1.5 border bg-white border-gray-200">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cm.created_by_color || '#94a3b8' }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 break-words">{cm.comment}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5" title={cm.created_at ? `${cm.created_at} (server time)` : undefined}>
                            {cm.created_by_name || 'Unknown'}
                            {cm.created_at ? ` · ${fmtCommentTime(cm.created_at)}` : ''}
                          </p>
                        </div>
                        <button type="button" onClick={() => removeComment(cm.id)}
                          className="text-gray-300 hover:text-red-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition">
                          <DeleteOutlineIcon style={{ fontSize: 14 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!loading && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-2">
            {!isCreate && (
              <button type="button" onClick={handleDelete} disabled={busy}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50">
                <DeleteOutlineIcon style={{ fontSize: 16 }} /> Delete
              </button>
            )}
            <div className="flex-1" />
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={save} disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}>
              {saving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentEntryModal;
